import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import * as api from './api.ts';
import { GlassPanel, InlineAction } from './ui.tsx';
import { WarningKindIcon } from './warning-icons.tsx';

/**
 * The person's own warning filter (Trav 2026-09-29): which kinds of agency
 * warning are drawn on their map, and which they are notified about — a card
 * in their Home notifications and, for an owner, admin or viewer, a push to
 * their phone ("dashboard users get both alerts on dashboard and phone").
 *
 * Two independent choices per kind, stored as exclusions on the server
 * (migration 0136) so a kind the ingest learns later is shown by default.
 * `muted` governs both the Home strip and the phone push (W2 as amended
 * 2026-09-29; the worker's sweep is api/src/warnings/notify.ts, migration
 * 0137, and applies the same floor as `isMuted()` below).
 *
 * Muting has a floor: an Emergency Warning (Extreme) or Watch and Act
 * (Severe) always raises its card and its push, whatever was muted — a pause never
 * suppresses a live event, and a warning at that level covering your own
 * building is the one interruption the dashboard owes you. Road closures are
 * the exception to the Severe half: the road adapters mark every closure
 * Severe, which would make muting Road meaningless, and a closed road is not
 * a warning to act.
 *
 * What this never does: rewords or re-attributes a warning (W1 — it only
 * decides whether one is drawn), or reaches the activation path (W3). A muted
 * warning still shows on each building's own page: muting quiets the
 * interruption, it never removes the fact.
 */

/**
 * The groups the filter reads in — grouping and labels only. Which kinds are
 * offered comes from the server (`kinds` on the GET), so a kind the ingest
 * learns later appears under Other rather than going missing.
 */
export const WARNING_GROUPS: { name: string; kinds: string[] }[] = [
  {
    name: 'Weather',
    kinds: [
      'storm',
      'wind',
      'rain',
      'flood',
      'cyclone',
      'tornado',
      'heat',
      'cold',
      'snow',
      'fog',
      'dust',
      'marine',
    ],
  },
  { name: 'Fire', kinds: ['fire'] },
  { name: 'Road', kinds: ['road'] },
  { name: 'Other', kinds: ['tsunami', 'earthquake', 'rescue', 'other'] },
];

const ALL_KINDS: string[] = WARNING_GROUPS.flatMap((g) => g.kinds);

const LABELS: Record<string, string> = {
  flood: 'Flood',
  storm: 'Storm',
  wind: 'Wind',
  rain: 'Rain',
  cyclone: 'Cyclone',
  tornado: 'Tornado',
  fire: 'Fire',
  heat: 'Heat',
  cold: 'Cold',
  snow: 'Snow',
  marine: 'Marine',
  fog: 'Fog',
  dust: 'Dust',
  tsunami: 'Tsunami',
  earthquake: 'Earthquake',
  road: 'Road',
  rescue: 'Rescue',
  other: 'Other',
};

const label = (kind: string) => LABELS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1);

/** The server's kinds laid into groups; anything the groups do not name is Other. */
function groupsFor(kinds: readonly string[]): { name: string; kinds: string[] }[] {
  const known = new Set(ALL_KINDS);
  const extra = kinds.filter((k) => !known.has(k));
  return WARNING_GROUPS.map((g) => ({
    name: g.name,
    kinds: [...g.kinds.filter((k) => kinds.includes(k)), ...(g.name === 'Other' ? extra : [])],
  })).filter((g) => g.kinds.length > 0);
}

/**
 * The kind a filter matches on: the server's own `event_kind`, verbatim. Never
 * the icon's fallback — a kind this build does not know (say 'volcano') must
 * not be swallowed by a hidden or muted 'other'. Only the icon falls back.
 */
export function kindOf(w: { event_kind: string | null | undefined }): string {
  return w.event_kind || 'other';
}

/**
 * Whether a warning's Home card is held back by the person's mutes. Emergency
 * Warning and Watch and Act levels are never held back (see the module note).
 */
export function isMuted(
  w: { event_kind: string | null | undefined; cap_severity: string },
  muted: ReadonlySet<string>,
): boolean {
  const kind = kindOf(w);
  if (!muted.has(kind)) return false;
  if (w.cap_severity === 'Extreme') return false;
  if (w.cap_severity === 'Severe' && kind !== 'road') return false;
  return true;
}

/**
 * "Show minor warnings" (round 3 #19): the server answers with high-relevance
 * warnings only (migration 0154 — Emergency Warning, Watch and Act, Severe,
 * and every earthquake or tsunami); minor ones (sheep graziers, frost, flood
 * watches, marine, road closures…) come back only when asked. A per-person,
 * per-browser convenience, so it lives in localStorage — and a storage that
 * throws (private window, blocked site data) just means "off".
 */
const SHOW_MINOR_KEY = 'evac.warnings.showMinor';
const minorListeners = new Set<() => void>();

function readShowMinor(): boolean {
  try {
    return window.localStorage.getItem(SHOW_MINOR_KEY) === '1';
  } catch {
    return false;
  }
}

let showMinorValue = typeof window === 'undefined' ? false : readShowMinor();

export function setShowMinorWarnings(on: boolean): void {
  showMinorValue = on;
  try {
    if (on) window.localStorage.setItem(SHOW_MINOR_KEY, '1');
    else window.localStorage.removeItem(SHOW_MINOR_KEY);
  } catch {
    // Remembered for this tab only.
  }
  minorListeners.forEach((l) => l());
}

/** Whether this person has asked to see minor warnings too. */
export function useShowMinorWarnings(): boolean {
  return useSyncExternalStore(
    (l) => {
      minorListeners.add(l);
      return () => minorListeners.delete(l);
    },
    () => showMinorValue,
    () => false,
  );
}

const KEY = ['warning-preferences'] as const;

type Prefs = { hiddenOnMap: string[]; muted: string[] };

export interface WarningFilter {
  /** The kinds the server offers, in its order (the full list before a read). */
  kinds: readonly string[];
  /** Kinds not drawn on the map. */
  hidden: ReadonlySet<string>;
  /** Kinds whose ordinary warnings raise no Home card. */
  muted: ReadonlySet<string>;
  /**
   * Whether the preference has been read (or failed to read). Until then the
   * strip holds back rather than flashing cards the person muted.
   */
  settled: boolean;
  /**
   * The saved filter could not be read. Everything shows, and the controls
   * are disabled — saving over a filter we could not read would erase it.
   */
  unreadable: boolean;
  /** A save that failed and was reverted, in words. Null when there is none. */
  error: string | null;
  /** Save a change computed from the latest state, never a stale render. */
  update: (change: (prev: Prefs) => Prefs) => void;
  /** Minor (low-relevance) warnings are asked for too. Local to this browser. */
  showMinor: boolean;
  setShowMinor: (on: boolean) => void;
}

/**
 * Read and save the filter. Optimistic: the map and strip change on the click.
 * Each change is computed from the cache at the moment of the click, so fast
 * clicks compose rather than overwrite; saves run one at a time (`scope`); and
 * once the last save settles the filter is re-read from the server, so the
 * screen always ends up agreeing with what is stored. A failed save says so.
 */
export function useWarningFilter(): WarningFilter {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: KEY,
    queryFn: () => api.getWarningPreferences(),
    staleTime: 5 * 60_000,
  });
  const mutation = useMutation({
    mutationKey: KEY,
    mutationFn: (next: Prefs) => api.putWarningPreferences(next),
    scope: { id: 'warning-preferences' },
    onError: () => {
      setError('Your warning filter could not be saved, so it is back to how it was. Try again.');
    },
    onSuccess: () => setError(null),
    onSettled: () => {
      // Only the last save in flight re-reads; earlier ones would overwrite
      // a later click's optimistic state with a server that has not seen it.
      if (qc.isMutating({ mutationKey: KEY }) <= 1) void qc.invalidateQueries({ queryKey: KEY });
    },
  });

  const data = query.data;
  const kinds = data?.kinds && data.kinds.length > 0 ? data.kinds : ALL_KINDS;
  const hidden = useMemo(() => new Set(data?.hiddenOnMap ?? []), [data]);
  const muted = useMemo(() => new Set(data?.muted ?? []), [data]);
  const showMinor = useShowMinorWarnings();
  return {
    showMinor,
    setShowMinor: setShowMinorWarnings,
    kinds,
    hidden,
    muted,
    settled: query.isSuccess || query.isError,
    unreadable: query.isError && !query.data,
    error,
    update: (change) => {
      const prev = qc.getQueryData<api.WarningPreferences>(KEY);
      const next = change({ hiddenOnMap: prev?.hiddenOnMap ?? [], muted: prev?.muted ?? [] });
      void qc.cancelQueries({ queryKey: KEY });
      qc.setQueryData<api.WarningPreferences>(KEY, { kinds: prev?.kinds ?? kinds, ...next });
      mutation.mutate(next);
    },
  };
}

/**
 * The bottom-left control over the map: "Warnings (N) ▾" opening a small
 * glass panel with one row per kind and two checkboxes each. N is what the map
 * is drawing now; when anything is hidden it reads "N of M shown", so a hidden
 * layer never reads as a clear sky.
 */
export function WarningsFilterControl({
  filter,
  shownCount,
  totalCount,
  counts,
  onHideAll,
}: {
  filter: WarningFilter;
  /** Warnings the map is drawing right now. */
  shownCount: number;
  /** In-force warnings around the estate, before filtering. */
  totalCount: number;
  /** In-force warnings around the estate, per kind, before filtering. */
  counts: ReadonlyMap<string, number>;
  /** "Hide all from map" was chosen — a focused warning is let go. */
  onHideAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape closes and hands focus back; a click or focus anywhere outside
  // closes, so Shift-Tab out of the panel does not leave it hanging open.
  useEffect(() => {
    if (!open) return;
    const outside = (t: EventTarget | null) =>
      t instanceof Node && !panelRef.current?.contains(t) && !buttonRef.current?.contains(t);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    const onDown = (e: PointerEvent) => {
      if (outside(e.target)) setOpen(false);
    };
    const onFocus = (e: FocusEvent) => {
      if (outside(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('focusin', onFocus);
    };
  }, [open]);

  // Opening moves focus to the first checkbox — never onto a shortcut, where
  // a stray Enter would rewrite the whole filter.
  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, [open]);

  const toggle = (kind: string, list: keyof Prefs) =>
    filter.update((prev) => {
      const next = new Set(prev[list]);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return { ...prev, [list]: [...next] };
    });

  const anyHidden = shownCount < totalCount;
  const buttonText = anyHidden
    ? `Warnings: ${shownCount} of ${totalCount} shown`
    : `Warnings (${shownCount})`;

  return (
    <div className="relative">
      <GlassPanel elevation="medium" className="notif-in">
        <button
          ref={buttonRef}
          type="button"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((o) => !o)}
          className="inline-flex h-9 items-center gap-1.5 rounded-[var(--radius-1)] px-3 text-[13px] font-medium text-[var(--ink)] hover:bg-[var(--paper-3)]"
        >
          {buttonText}
          <span aria-hidden>▾</span>
        </button>
        {filter.error ? (
          <p
            role="alert"
            className="max-w-[280px] px-3 pb-2 text-[12px] leading-[16px] text-[var(--ink)]"
          >
            {filter.error}
          </p>
        ) : null}
      </GlassPanel>

      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="group"
          aria-label="Warning filter"
          className="absolute bottom-full left-0 mb-2 w-[340px] max-w-[calc(100vw-32px)]"
        >
          <GlassPanel elevation="high" className="notif-in">
            <div className="max-h-[min(70vh,560px)] overflow-y-auto px-4 py-3">
              <p className="text-[14px] font-semibold text-[var(--ink)]">Warnings</p>
              <p className="mt-1 text-[12px] leading-[16px] text-[var(--ink-2)]">
                Choose which kinds this dashboard draws on your map and which you are notified
                about. Emergency Warnings and Watch and Act always notify you, and every warning
                still shows on each building&rsquo;s page.
              </p>
              {filter.unreadable ? (
                <p role="status" className="mt-2 text-[12px] leading-[16px] text-[var(--ink)]">
                  Your saved filter could not be loaded, so everything is showing. Changes are off
                  until it loads.
                </p>
              ) : null}
              <label className="mt-3 flex min-h-[32px] cursor-pointer items-center justify-between gap-3 border-b border-[var(--line)] pb-2 text-[13px] text-[var(--ink)]">
                <span>
                  Show minor warnings
                  <span className="block text-[11px] leading-[14px] text-[var(--ink-2)]">
                    Advice levels, frost, sheep graziers, flood watches, marine and road closures.
                    Earthquakes always show.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={filter.showMinor}
                  onChange={(e) => filter.setShowMinor(e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-[var(--ink)]"
                />
              </label>
              <div
                aria-hidden
                className="mt-3 grid grid-cols-[1fr_56px_64px] text-[12px] font-medium text-[var(--ink)]"
              >
                <span />
                <span className="text-center">On map</span>
                <span className="text-center">Notify me</span>
              </div>
              <p className="mt-0.5 text-right text-[11px] leading-[14px] text-[var(--ink-2)]">
                On your Home screen and your phone
              </p>
              {groupsFor(filter.kinds).map((group) => (
                <fieldset key={group.name} className="mt-2" disabled={filter.unreadable}>
                  <legend className="text-[12px] font-semibold text-[var(--ink)]">
                    {group.name}
                  </legend>
                  {group.kinds.map((kind) => {
                    const n = counts.get(kind) ?? 0;
                    const name = label(kind);
                    return (
                      <div
                        key={kind}
                        className="grid min-h-[32px] grid-cols-[1fr_56px_64px] items-center"
                      >
                        <span className="flex min-w-0 items-center gap-2 text-[13px] text-[var(--ink)]">
                          <WarningKindIcon kind={kind} size={14} />
                          <span className="truncate">{name}</span>
                          {n > 0 ? (
                            <span className="text-[12px] text-[var(--ink-2)]">{n} now</span>
                          ) : null}
                        </span>
                        <Check
                          checked={!filter.hidden.has(kind)}
                          label={`Show ${name.toLowerCase()} warnings on the map`}
                          onChange={() => toggle(kind, 'hiddenOnMap')}
                        />
                        <Check
                          checked={!filter.muted.has(kind)}
                          label={`Notify me about ${name.toLowerCase()} warnings, on my Home screen and my phone`}
                          onChange={() => toggle(kind, 'muted')}
                        />
                      </div>
                    );
                  })}
                </fieldset>
              ))}
              {filter.unreadable ? null : (
                <div className="mt-3 flex flex-wrap gap-x-3 border-t border-[var(--line)] pt-2">
                  <InlineAction
                    label="Show every kind on the map and notify me about every kind"
                    onClick={() => filter.update(() => ({ hiddenOnMap: [], muted: [] }))}
                  >
                    Show everything
                  </InlineAction>
                  <InlineAction
                    label="Hide every kind listed here from the map"
                    onClick={() => {
                      onHideAll();
                      filter.update((prev) => ({ ...prev, hiddenOnMap: [...filter.kinds] }));
                    }}
                  >
                    Hide all from map
                  </InlineAction>
                </div>
              )}
            </div>
          </GlassPanel>
        </div>
      ) : null}
    </div>
  );
}

/** A labelled checkbox with a 32px target (WCAG 2.5.8). */
function Check({
  checked,
  label: name,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <label className="flex h-8 cursor-pointer items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        aria-label={name}
        className="h-4 w-4 cursor-pointer accent-[var(--ink)]"
      />
    </label>
  );
}
